import L from 'leaflet'

/**
 * The pins, and the tile layer, shared by the two maps in this app.
 *
 * Leaflet's default marker is three PNGs it resolves by sniffing its own script URL.
 * Under Vite that lookup lands on nothing and the pin renders as a broken image, so
 * every pin here is drawn as an SVG instead — in the brand colour, using the same
 * filled-node / ring-node vocabulary as `JourneyRail`.
 *
 * Extracted from `LeafletMap` when `TrackingMap` arrived. Pin art is the one thing the
 * picker and the tracker genuinely share; everything else about them is opposite.
 */

const PLACE = (inner) => `<svg width="30" height="40" viewBox="0 0 28 37" xmlns="http://www.w3.org/2000/svg">
  <path d="M14 36S26 22.8 26 13.8A12 12 0 1 0 2 13.8C2 22.8 14 36 14 36Z"
        fill="#3C0008" stroke="#FFFCFA" stroke-width="2" stroke-linejoin="round"/>
  ${inner}
</svg>`

/**
 * A rider is not a place, so a rider is not a teardrop.
 *
 * The pickup and the dropoff are pinned *to the ground* — that is what the pointed tail
 * means, and it is why they sit with their tip on the coordinate. A rider is somewhere at
 * a moment and somewhere else a minute later, and a shape that claims to be nailed down
 * would be saying the wrong thing about the one pin on the map that moves. So: a disc,
 * centred on the coordinate rather than standing on it.
 */
const RIDER = `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
  <circle cx="14" cy="14" r="11" fill="#3C0008" stroke="#FFFCFA" stroke-width="2.5"/>
  <circle cx="14" cy="14" r="4" fill="#FFF0EB"/>
</svg>`

const SHAPES = {
  pickup: {
    html: PLACE('<circle cx="14" cy="13.5" r="4.75" fill="#FFF0EB"/>'),
    size: [30, 40],
    // The tip of the teardrop is the coordinate, not its middle.
    anchor: [15, 39],
  },
  dropoff: {
    html: PLACE('<circle cx="14" cy="13.5" r="4.25" fill="none" stroke="#FFF0EB" stroke-width="2.5"/>'),
    size: [30, 40],
    anchor: [15, 39],
  },
  rider: {
    html: RIDER,
    size: [28, 28],
    // Dead centre, because the disc surrounds the position rather than pointing at it.
    anchor: [14, 14],
  },
}

export const pinFor = (variant) => {
  const shape = SHAPES[variant] ?? SHAPES.pickup

  return L.divIcon({
    className: 'mx-pin',
    html: shape.html,
    iconSize: shape.size,
    iconAnchor: shape.anchor,
  })
}

/**
 * OpenStreetMap's own tiles: no key, no account, no bill. The whole reason this app can
 * draw a map at all while staying inside free tiers.
 */
export const addTiles = (map) => L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '© OpenStreetMap contributors',
  maxZoom: 19,
}).addTo(map)
